import {
  type IBrand,
  type ICustomReport,
  type IOrganisation,
  type IPractice,
  type IQueryScopeRequests,
} from '@principle-theorem/principle-core/interfaces';
import {
  IQueryScope,
  IReportingQuery,
  IReportingQueryRequest,
} from '@principle-theorem/reporting/interfaces';
import {
  DataStreamEvent,
  IDataStreamEvent,
  IStorageResponseAPI,
  ITimePeriod,
  snapshot,
  type DocumentReference,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { from, type Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

export interface IBigQueryRequestMetadata {
  customReportRef?: DocumentReference<ICustomReport>;
  filterDate?: moment.Moment;
  filterDateRange?: ITimePeriod;
}

export class BigQueryDataSource<FactRow> {
  constructor(
    private _api: IStorageResponseAPI,
    private _orgRef$: Observable<DocumentReference<IOrganisation>>
  ) {}

  async buildAllScopeRequests(
    brandRef$: Observable<DocumentReference<IBrand>>,
    practiceRefs$: Observable<DocumentReference<IPractice>[]>
  ): Promise<IQueryScopeRequests> {
    const brandRef = await snapshot(brandRef$);
    const practiceRefs = await snapshot(practiceRefs$);
    return {
      brand: { brandRef },
      practice: { practiceRefs },
    };
  }

  async build(
    query: IReportingQuery,
    scopeRequests: IQueryScopeRequests,
    metadata?: IBigQueryRequestMetadata
  ): Promise<FactRow[]> {
    try {
      const response$ = this.get$(query, scopeRequests, metadata).pipe(
        filter(DataStreamEvent.isComplete),
        map((event) => event.value)
      );
      return await snapshot(response$);
    } catch {
      return [];
    }
  }

  get$(
    query: IReportingQuery,
    scopeRequests: IQueryScopeRequests,
    metadata?: IBigQueryRequestMetadata
  ): Observable<IDataStreamEvent<FactRow[]>> {
    const fiveMinutes = 300000;
    return from(this.getQueryScope(scopeRequests)).pipe(
      map(
        (scope): IReportingQueryRequest => ({
          scope,
          query,
          ...metadata?.customReportRef
            ? { customReportRef: metadata.customReportRef }
            : {},
          ...metadata?.filterDate
            ? { filterDate: metadata.filterDate }
            : {},
          ...metadata?.filterDateRange
            ? { filterDateRange: metadata.filterDateRange }
            : {},
        })
      ),
      switchMap((request) =>
        this._api.get$<IReportingQueryRequest, FactRow[]>(
          'http-reporting-queryFn',
          request,
          {
            timeout: fiveMinutes,
          }
        )
      )
    );
  }

  async getQueryScope(
    scopeRequests: IQueryScopeRequests
  ): Promise<IQueryScope> {
    return {
      orgRef: await snapshot(this._orgRef$),
      scopeRequests,
    };
  }
}
